// src/pages/user/Profile.jsx
import React, { useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useToast } from "../../hooks/useToast";
import Loading from "../../components/common/Loading";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useUpdateUsername } from "../../hooks/user/useUpdateUsername";
import { useUpdateProfilePicture } from "../../hooks/user/useUpdateProfilePicture";
import { useUsernameValidation } from "../../hooks/user/useUsernameValidation";
import VerificationBadge from "../../components/user/VerificationBadge";
import FollowersModal from "../../components/user/FollowersModal";
import { NotificationPreferences } from "../../components/notifications";
import { useNotificationPreferences } from "../../hooks/notifications/useNotificationPreferences";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faRightFromBracket,
  faPen,
  faUsers,
  faMedal,
  faCrown,
  faEnvelope,
  faCalendarDays,
  faUser,
  faBell,
  faChartBar,
  faCog,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../../components/common/PageHeader";

const Stat = ({ icon, label, value }) => {
  let content;

  if (value == null) {
    content = "—";
  } else if (typeof value === "object" && !Array.isArray(value)) {
    // Special-case a rank-like object
    const { name, points, progress } = value;
    const formattedPoints =
      typeof points === "number" ? points.toFixed(2) : points;
    const header = [
      name,
      typeof points === "number" ? `${formattedPoints} pts` : null,
    ]
      .filter(Boolean)
      .join(" • ");

    content = (
      <div className="w-full">
        <div className="text-dark-grey font-semibold">{header || "—"}</div>
        {typeof progress === "number" && (
          <div className="mt-1 h-2 bg-light-grey rounded">
            <div
              className="h-2 bg-deep-red rounded"
              style={{
                width: `${Math.max(0, Math.min(100, progress * 100))}%`,
              }}
            />
          </div>
        )}
      </div>
    );
  } else if (Array.isArray(value)) {
    content = value.join(", ");
  } else {
    content = String(value);
  }

  return (
    <div className="flex items-center gap-3">
      <div className="shrink-0 rounded-full bg-light-grey p-2 shadow-card">
        <FontAwesomeIcon icon={icon} className="text-medium-grey" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-medium-grey">{label}</div>
        <div className="break-words">{content}</div>
      </div>
    </div>
  );
};

const TABS = [
  { id: "overview", label: "Overview", icon: faUser },
  { id: "settings", label: "Settings", icon: faCog },
  { id: "account", label: "Account", icon: faShieldAlt },
];

const Profile = () => {
  const { logout } = useAuth0();
  const toast = useToast();
  const { data: user, isLoading, error, refetch } = useFetchUser();
  const { data: notificationPreferences } = useNotificationPreferences();
  const updateUsernameMutation = useUpdateUsername();
  const updateProfilePictureMutation = useUpdateProfilePicture();
  const [newUsername, setNewUsername] = useState("");
  const [followersModalOpen, setFollowersModalOpen] = useState(false);
  const [followersModalTab, setFollowersModalTab] = useState("followers");
  const [activeTab, setActiveTab] = useState("overview");

  // Use username validation hook for availability checking
  const { validateUsername, isValidating, validationError, isValid } =
    useUsernameValidation();

  const joinedDate = useMemo(() => {
    if (!user?.createdAt) return null;
    try {
      return new Date(user.createdAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return null;
    }
  }, [user?.createdAt]);

  const leaguesCount =
    user?.activeLeagueCount ??
    user?.totalLeagues ??
    (Array.isArray(user?.leagues) ? user.leagues.length : undefined);
  const globalRank = user?.globalRank;

  // Absolute fill toward the next rank threshold (e.g. 101.97 / 115).
  // At the top rank (no nextPoints) the bar is full.
  const rankFill =
    typeof globalRank === "object" && typeof globalRank?.nextPoints === "number"
      ? Math.max(
          0,
          Math.min(1, (globalRank.points || 0) / globalRank.nextPoints),
        )
      : 1;

  const handleUsernameInputChange = (e) => {
    const value = e.target.value;
    setNewUsername(value);
    validateUsername(value);
  };

  const handleUpdateUsername = () => {
    if (!newUsername?.trim()) {
      toast.info("Enter a username first");
      return;
    }

    if (!isValid) {
      toast.error(validationError || "Please enter a valid username");
      return;
    }

    updateUsernameMutation.mutate(newUsername.trim(), {
      onSuccess: () => {
        setNewUsername("");
        refetch();
      },
      onError: (e) =>
        toast.error(`Failed to update username: ${e?.message ?? ""}`),
    });
  };

  const handleProfilePictureChange = (e) => {
    const imageFile = e.target.files?.[0];
    if (!imageFile || !user?.auth0Id) return;
    updateProfilePictureMutation.mutate(
      { userId: user.auth0Id, imageFile },
      {
        onSuccess: () => {
          toast.success("Profile picture updated");
          refetch();
        },
        onError: (er) =>
          toast.error(`Failed to update picture: ${er?.message ?? ""}`),
      },
    );
  };

  const handleOpenFollowersModal = (tab = "followers") => {
    setFollowersModalTab(tab);
    setFollowersModalOpen(true);
  };

  const handleCloseFollowersModal = () => {
    setFollowersModalOpen(false);
  };

  if (isLoading) return <Loading />;
  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-light-grey">
        <div className="p-6 max-w-md text-center bg-white rounded-card shadow-card">
          <div className="text-2xl text-deep-red mb-3">Error</div>
          <p className="text-medium-grey mb-4">
            {error.message || "Could not load profile."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-deep-red hover:bg-bright-red text-white rounded-md shadow transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Profile"
        subtitle="Manage your account settings"
        icon={faUser}
        backgroundIcon={faUser}
      />

      {/* Full-screen loading overlay for profile picture upload */}
      {updateProfilePictureMutation.isLoading && (
        <div className="fixed inset-0 z-50 bg-dark-grey/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-card shadow-card p-8 max-w-sm mx-4">
            <div className="flex flex-col items-center">
              {/* Emblem with animation */}
              <div className="relative w-20 h-20 flex items-center justify-center overflow-hidden mb-4">
                <img
                  src="/emblem-gold.png"
                  alt="DayShift Sports Emblem"
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
              </div>

              {/* Upload message */}
              <h3 className="text-xl font-semibold text-dark-grey mb-2">
                Uploading Profile Picture
              </h3>
              <p className="text-medium-grey text-center">
                Please wait while we update your profile...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Profile Header Card */}
      <div className="bg-white rounded-card shadow-card p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Avatar */}
          <div className="relative w-24 h-24 shrink-0">
            <img
              src={user?.picture}
              alt={user?.username || "User avatar"}
              className="w-24 h-24 rounded-full object-cover ring-4 ring-light-grey"
            />
            <label
              htmlFor="profile-picture-input"
              className={`absolute -bottom-2 -right-2 bg-white text-dark-grey border border-medium-grey rounded-full p-2 shadow-card ${
                updateProfilePictureMutation.isLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-light-grey cursor-pointer"
              }`}
              title={
                updateProfilePictureMutation.isLoading
                  ? "Uploading..."
                  : "Change profile photo"
              }
            >
              <FontAwesomeIcon icon={faCamera} />
            </label>
            <input
              type="file"
              id="profile-picture-input"
              accept="image/*"
              onChange={handleProfilePictureChange}
              disabled={updateProfilePictureMutation.isLoading}
              className="hidden"
            />
          </div>

          {/* Identity */}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 text-xl font-semibold text-dark-grey">
              {user?.username || user?.name || "User"}
              <VerificationBadge
                verified={user?.verified || false}
                size="medium"
                showTooltip={true}
              />
            </div>
            {user?.username && (
              <div className="text-medium-grey">@{user.username}</div>
            )}
            {user?.role && (
              <span className="inline-flex mt-2 items-center px-3 py-1 text-xs font-medium rounded-full bg-light-gold text-gold shadow-card">
                <FontAwesomeIcon icon={faCrown} className="mr-2" />
                {user.role}
              </span>
            )}
          </div>

          {/* Social stats summary */}
          <div className="flex gap-6 shrink-0">
            <button
              className="text-center hover:bg-light-grey/50 rounded-lg px-3 py-2 transition-colors"
              onClick={() => handleOpenFollowersModal("followers")}
            >
              <div className="text-xl font-bold text-deep-red">
                {user?.followerCount ?? 0}
              </div>
              <div className="text-xs text-medium-grey">Followers</div>
            </button>
            <button
              className="text-center hover:bg-light-grey/50 rounded-lg px-3 py-2 transition-colors"
              onClick={() => handleOpenFollowersModal("following")}
            >
              <div className="text-xl font-bold text-deep-red">
                {user?.followingCount ?? 0}
              </div>
              <div className="text-xs text-medium-grey">Following</div>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex bg-dark-grey rounded-lg shadow-card mb-6 overflow-x-auto touch-pan-x">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 flex flex-col items-center transition-colors ${
              activeTab === tab.id
                ? "text-gold border-b-2 border-gold"
                : "text-white/80 hover:text-white"
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="mb-1" />
            <span className="text-sm font-medium">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Rank Card */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h3 className="text-lg font-semibold text-dark-grey mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faMedal} className="text-medium-grey" />
              Global Rank
            </h3>
            {typeof globalRank === "object" &&
            typeof globalRank?.progress === "number" ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative flex-1 h-6 bg-light-grey rounded-full overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-green-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.round(rankFill * 100)}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-between px-2">
                      <span
                        className={`text-[11px] font-semibold z-10 ${rankFill > 0.15 ? "text-white" : "text-dark-grey"}`}
                      >
                        {globalRank.name}
                      </span>
                      <span
                        className={`text-[11px] z-10 ${rankFill > 0.45 ? "text-white" : "text-dark-grey"}`}
                      >
                        →
                      </span>
                      <span
                        className={`text-[11px] font-semibold z-10 ${rankFill > 0.85 ? "text-white" : "text-dark-grey"}`}
                      >
                        {globalRank.nextRank || "MAX"}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-medium-grey">
                  {globalRank.nextRank &&
                  typeof globalRank.nextPoints === "number"
                    ? `${globalRank.points?.toFixed(2)} / ${globalRank.nextPoints} pts`
                    : "Max rank reached"}
                </p>
              </div>
            ) : (
              <div className="text-dark-grey">
                {typeof globalRank === "object"
                  ? globalRank?.name || "—"
                  : (globalRank ?? "—")}
              </div>
            )}
          </div>

          {/* Info Card */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h3 className="text-lg font-semibold text-dark-grey mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faChartBar} className="text-medium-grey" />
              Details
            </h3>
            <div className="space-y-4">
              <Stat icon={faEnvelope} label="Email" value={user?.email} />
              <Stat icon={faCalendarDays} label="Joined" value={joinedDate} />
              <Stat
                icon={faUsers}
                label="Leagues"
                value={leaguesCount ?? "—"}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "settings" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Update Username */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h3 className="text-lg font-semibold text-dark-grey mb-4">
              Update Username
            </h3>
            <div className="flex flex-col gap-3">
              <div className="relative">
                <input
                  type="text"
                  value={newUsername}
                  onChange={handleUsernameInputChange}
                  placeholder={user?.username || "Set your username"}
                  className={`
                    w-full px-4 py-2 pr-10 border rounded-md
                    focus:ring-2 focus:outline-none
                    ${
                      validationError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : isValid
                          ? "border-green-500 focus:border-green-500 focus:ring-green-500"
                          : "border-medium-grey focus:border-deep-red focus:ring-deep-red"
                    }
                  `}
                />

                {/* Visual Indicators */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isValidating && (
                    <div className="animate-spin h-4 w-4 border-2 border-deep-red border-t-transparent rounded-full" />
                  )}
                  {!isValidating && isValid && (
                    <svg
                      className="h-5 w-5 text-green-600"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {!isValidating && validationError && newUsername && (
                    <svg
                      className="h-5 w-5 text-red-600"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Validation Feedback */}
              {validationError && newUsername && (
                <p className="text-sm text-red-600 flex items-start">
                  <svg
                    className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {validationError}
                </p>
              )}
              {!validationError && isValid && (
                <p className="text-sm text-green-600 flex items-center">
                  <svg
                    className="h-4 w-4 mr-1 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Username available
                </p>
              )}
              {!validationError && !isValid && newUsername && !isValidating && (
                <p className="text-sm text-medium-grey">
                  Enter 3-20 characters (letters, numbers, _ or -)
                </p>
              )}

              <button
                onClick={handleUpdateUsername}
                disabled={
                  updateUsernameMutation.isLoading || !isValid || isValidating
                }
                className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-md text-white bg-deep-red hover:bg-bright-red disabled:opacity-60 disabled:cursor-not-allowed"
                title="Save username"
              >
                <FontAwesomeIcon icon={faPen} />
                {updateUsernameMutation.isLoading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="shrink-0 rounded-full bg-light-grey p-2 shadow-card">
                <FontAwesomeIcon icon={faBell} className="text-medium-grey" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-grey">
                  Notification Settings
                </h3>
                <p className="text-sm text-medium-grey">
                  Manage your notification preferences
                </p>
              </div>
            </div>
            <NotificationPreferences
              preferences={
                notificationPreferences?.preferences || { follow: true }
              }
              autoSave={true}
            />
          </div>
        </div>
      )}

      {activeTab === "account" && (
        <div className="max-w-md">
          <div className="bg-white rounded-card shadow-card p-6">
            <h3 className="text-lg font-semibold text-dark-grey mb-4">
              Session
            </h3>
            <p className="text-medium-grey mb-4">
              Sign out of your account on this device.
            </p>
            <button
              onClick={() =>
                logout({
                  logoutParams: {
                    returnTo:
                      process.env.REACT_APP_PUBLIC_BASE_URL ||
                      window.location.origin,
                  },
                })
              }
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2 rounded-md text-white bg-deep-red hover:bg-bright-red"
            >
              <FontAwesomeIcon icon={faRightFromBracket} />
              Log Out
            </button>
          </div>
        </div>
      )}

      {/* Followers Modal */}
      <FollowersModal
        isOpen={followersModalOpen}
        onClose={handleCloseFollowersModal}
        userId={user?._id || user?.id}
        username={user?.username}
        initialTab={followersModalTab}
        followerCount={user?.followerCount ?? 0}
        followingCount={user?.followingCount ?? 0}
      />
    </div>
  );
};

export default Profile;
